import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import QRCode from 'qrcode'
import {
  ArrowLeft, Pencil, Loader2, QrCode, Printer, Link2, CalendarClock, ShieldCheck,
  MapPin, Package, Clock, TrendingDown, ShieldAlert, ScanLine, Calculator, AlertTriangle,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson } from '../lib/api.js'
import {
  statusInfo, assetAge, fmtDateTH, fmtMoney, indexTypes, typeFull, relabelBranch,
  assetDeepLink, assetLabelLine, parseDate, checkDueState,
  profileImage, imageThumbSrc, warrantyState, depreciationEstimate, usefulLife,
  isDepreciable, acctClassInfo,
} from '../lib/asset.js'
import AssetEditor from '../components/asset/AssetEditor.jsx'
import AssetChecks from '../components/asset/AssetChecks.jsx'
import AssetImages from '../components/asset/AssetImages.jsx'
import AssetMaintenance from '../components/asset/AssetMaintenance.jsx'
import { openLabelPrint } from '../lib/assetLabel.js'
import { verifyScan } from '../lib/assetScan.js'

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-50 last:border-0">
      <span className="text-[13px] text-slate-400 shrink-0">{label}</span>
      <span className="text-[13.5px] text-slate-700 text-right min-w-0">{children}</span>
    </div>
  )
}

function Card({ icon: Icon, title, children, right }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center"><Icon size={16} strokeWidth={2} /></span>
          <h3 className="text-[14.5px] font-semibold text-slate-800">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </section>
  )
}

export default function AssetDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  // `?u=` is only ever appended by a QR label, so its presence means "this page
  // was opened by scanning a sticker" — i.e. someone is standing in front of the
  // machine, mid-check. Stickers printed before the dedicated /check screen land
  // here, so make that landing do the right thing instead of nothing.
  const [sp] = useSearchParams()
  const scannedUid = sp.get('u') || ''
  const fromScan = !!scannedUid
  const [asset, setAsset] = useState(null)
  const [types, setTypes] = useState([])
  const [branches, setBranches] = useState([])
  const [expense, setExpense] = useState(null)
  const [qr, setQr] = useState('')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    cachedGet('/api/asset-types').then((r) => setTypes(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  function load() {
    setLoading(true); setErr('')
    apiGet(`/api/assets/${id}`)
      .then((a) => {
        setAsset(a)
        if (a?.expense_id > 0) {
          apiGet(`/api/expense/${a.expense_id}`).then(setExpense).catch(() => setExpense(null))
        } else setExpense(null)
      })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [id])

  // Bring the check form into view once the record has rendered.
  useEffect(() => {
    if (!fromScan || !asset?.id) return
    const t = setTimeout(() => {
      document.getElementById('asset-checks')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 250)
    return () => clearTimeout(t)
  }, [fromScan, asset?.id])

  const { byId } = useMemo(() => indexTypes(types), [types])
  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name))
    return m
  }, [branches])

  // build the QR once the asset is known
  useEffect(() => {
    if (!asset?.id) return
    QRCode.toDataURL(assetDeepLink(asset), { margin: 1, width: 240, errorCorrectionLevel: 'M' })
      .then(setQr).catch(() => setQr(''))
  }, [asset])

  const [syncing, setSyncing] = useState(false)
  /* Never fires on its own: writing to an accounting record has to be a
   * deliberate click, because the accountant may have set that flag on purpose. */
  async function syncExpenseClass() {
    if (!asset?.expense_id) return
    setSyncing(true)
    try {
      await patchJson(`/api/expense/${asset.expense_id}`, { asset: isDepreciable(asset) })
      const fresh = await apiGet(`/api/expense/${asset.expense_id}`).catch(() => null)
      if (fresh) setExpense(fresh)
    } catch (e) {
      setErr(String(e.message || e))
    } finally { setSyncing(false) }
  }

  function printLabel() {
    if (!asset || !qr) return
    openLabelPrint([{ url: qr, line: assetLabelLine(asset, byId), serial: asset.serial_num, id: asset.id }])
  }

  if (loading) return <div className="px-10 py-16 text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</div>
  if (err) return <div className="px-10 py-16 text-rose-500">{err}</div>
  if (!asset) return <div className="px-10 py-16 text-slate-400">ไม่พบสินทรัพย์</div>

  const s = statusInfo(asset.status)
  const age = assetAge(asset.date)
  const due = checkDueState(asset)
  const warr = warrantyState(asset)
  const depr = depreciationEstimate(asset)
  const profile = profileImage(asset)
  const purchaseAmt = expense?.amount?.payment ?? expense?.amount?.total_price
  const verdict = fromScan ? verifyScan(asset, scannedUid) : 'unverified'
  const acct = acctClassInfo(asset)
  const deprec = isDepreciable(asset)
  /* The expense record carries its own `asset` flag, and that flag is what the
   * accountant's CSV export actually reads. If the two disagree, one of the two
   * books is wrong — surface it here rather than letting it be discovered at
   * year end. Only meaningful once the linked expense has loaded. */
  const expenseMismatch = expense && asset.expense_id > 0 && (!!expense.asset !== deprec)

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      {/* header */}
      <button onClick={() => navigate('/manager/assets')} className="flex items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft size={15} /> กลับไปที่ทะเบียนสินทรัพย์
      </button>
      {fromScan && (
        <div className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-3 mb-4 ${verdict === 'mismatch' ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
          {verdict === 'mismatch'
            ? <ShieldAlert size={17} className="text-rose-600 shrink-0 mt-0.5" />
            : <ShieldCheck size={17} className="text-emerald-600 shrink-0 mt-0.5" />}
          <div className={`text-[13px] leading-relaxed flex-1 min-w-0 ${verdict === 'mismatch' ? 'text-rose-700' : 'text-emerald-700'}`}>
            {verdict === 'mismatch'
              ? <><b>สติกเกอร์ไม่ตรงกับสินทรัพย์นี้</b> — รหัสใน QR ไม่ตรงกับทะเบียน อาจเป็นสติกเกอร์ที่ลอกมาติดผิดเครื่อง หรือป้ายถูกทำซ้ำ ตรวจสอบ Serial กับตัวเครื่องก่อนบันทึก</>
              : <>เปิดจากการสแกน QR — ยืนยันตรงกับทะเบียนแล้ว</>}
          </div>
          <button
            onClick={() => navigate(`/manager/assets/${asset.id}/check?u=${encodeURIComponent(scannedUid)}`)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700"
          >
            <ScanLine size={13} /> โหมดตรวจเช็ก
          </button>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          {profile
            ? <img src={imageThumbSrc(profile)} alt="" className="w-12 h-12 rounded-2xl object-cover ring-1 ring-slate-200 shrink-0" />
            : <span className="w-12 h-12 rounded-2xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><Package size={24} strokeWidth={1.75} /></span>}
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">{asset.name || '(ไม่มีชื่อ)'}</h1>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-medium ring-1 ${s.tone}`}>{s.label}</span>
              {due.due && (
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium ring-1 bg-amber-50 text-amber-700 ring-amber-200">
                  <Clock size={12} /> {due.never ? 'ยังไม่เคยตรวจ' : 'ถึงกำหนดตรวจ'}
                </span>
              )}
            </div>
            <p className="text-[13.5px] text-slate-500 mt-0.5">{typeFull(byId, asset.asset_type_id)}{(asset.brand || asset.model) ? ` · ${[asset.brand, asset.model].filter(Boolean).join(' ')}` : ''}</p>
          </div>
        </div>
        <button
          onClick={() => setEditing(true)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 shrink-0"
        >
          <Pencil size={15} /> แก้ไข
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* left: details */}
        <div className="lg:col-span-2 space-y-5">
          <AssetImages asset={asset} onChanged={load} />

          <Card icon={Package} title="รายละเอียด">
            <Row label="ชื่อ">{asset.name || '—'}</Row>
            <Row label="ประเภท">{typeFull(byId, asset.asset_type_id)}</Row>
            <Row label="ยี่ห้อ / รุ่น">{[asset.brand, asset.model].filter(Boolean).join(' · ') || '—'}</Row>
            <Row label="Serial Number"><span className="tabular-nums">{asset.serial_num || '—'}</span></Row>
            <Row label="สาขา">{branchName.get(Number(asset.branch_id)) || '—'}</Row>
            <Row label="ที่ตั้ง">{asset.location ? <span className="inline-flex items-center gap-1"><MapPin size={13} className="text-slate-400" />{asset.location}</span> : '—'}</Row>
            {asset.remark && <Row label="หมายเหตุ">{asset.remark}</Row>}
          </Card>

          <Card icon={CalendarClock} title="อายุและการจัดซื้อ">
            <Row label="วันที่ได้มา">{fmtDateTH(asset.date)}</Row>
            <Row label="อายุสินทรัพย์">{age ? <span className="font-medium text-slate-800">{age.label}</span> : '—'}</Row>
            <Row label="ราคาซื้อ">{asset.price ? `${fmtMoney(asset.price)} บาท` : '—'}</Row>
            <Row label="ประเภททางบัญชี">
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ${acct.tone}`}>
                <Calculator size={11} />{acct.label} · {acct.short}
              </span>
            </Row>
            {deprec && <Row label="อายุการใช้งาน">{usefulLife(asset)} ปี</Row>}
            <Row label="ประกันหมดอายุ">
              {warr.state === 'none'
                ? <span className="text-slate-400">ไม่มีประกัน</span>
                : <span className={`inline-flex items-center gap-1 ${warr.state === 'expired' ? 'text-rose-600' : warr.state === 'soon' ? 'text-amber-600' : 'text-emerald-600'}`}>
                    <ShieldCheck size={13} />{fmtDateTH(asset.warranty_expire)}
                    {warr.state === 'expired' ? ' (หมดแล้ว)' : warr.state === 'soon' ? ` (อีก ${warr.daysLeft} วัน)` : ''}
                  </span>}
            </Row>
            {!deprec && (
              <Row label="ค่าเสื่อม">
                <span className="text-slate-400">ไม่คิดค่าเสื่อม — ลงเป็นค่าใช้จ่ายทั้งจำนวนในงวดที่ซื้อ</span>
              </Row>
            )}
            {depr && (
              <Row label="มูลค่าคงเหลือ (ประเมิน)">
                <span className="inline-flex items-center gap-1">
                  <TrendingDown size={13} className="text-slate-400" />
                  {fmtMoney(depr.bookValue)} ฿
                  {depr.fullyDepreciated && <span className="text-[11px] text-slate-400">· ตัดครบแล้ว</span>}
                </span>
              </Row>
            )}
            {expenseMismatch && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 my-2">
                <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[12.5px] text-amber-800 leading-relaxed flex-1 min-w-0">
                  ใบซื้อ #{asset.expense_id} บันทึกเป็น <b>{expense.asset ? 'สินทรัพย์ (คิดค่าเสื่อม)' : 'ค่าใช้จ่าย'}</b>{' '}
                  แต่สินทรัพย์นี้ตั้งไว้เป็น <b>{acct.short}</b> — บัญชีจะอ่านสองที่ไม่ตรงกัน
                </div>
                <button
                  onClick={syncExpenseClass} disabled={syncing}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                >
                  {syncing ? <Loader2 size={12} className="animate-spin" /> : null} แก้ใบซื้อให้ตรงกัน
                </button>
              </div>
            )}
            <Row label="ผูกกับใบซื้อ">
              {asset.expense_id > 0
                ? <button onClick={() => navigate('/manager/expense')} className="inline-flex items-center gap-1 text-brand-600 hover:underline">
                    <Link2 size={13} /> {expense?.detail || `ใบซื้อ #${asset.expense_id}`}{purchaseAmt != null ? ` · ${fmtMoney(purchaseAmt)}฿` : ''}
                  </button>
                : '—'}
            </Row>
          </Card>

          {/* P2 — periodic checks with camera photo */}
          <AssetChecks asset={asset} onChanged={load} autoOpen={fromScan} />

          {/* P3 — maintenance workflow */}
          <AssetMaintenance asset={asset} onChanged={load} />
        </div>

        {/* right: QR */}
        <div className="space-y-5">
          <Card icon={QrCode} title="QR / บาร์โค้ด">
            <div className="flex flex-col items-center text-center">
              {qr
                ? <img src={qr} alt="QR code" className="w-44 h-44" />
                : <div className="w-44 h-44 flex items-center justify-center text-slate-300"><Loader2 size={20} className="animate-spin" /></div>}
              <p className="text-[12px] text-slate-400 mt-2 leading-relaxed">
                สแกนเพื่อเปิดหน้าสินทรัพย์นี้ ใช้ตอนตรวจเช็ก<br />(พิมพ์แล้วติดที่ตัวเครื่องได้ หรือใช้ค้นหาในระบบสำหรับของที่ติดสติกเกอร์ไม่ได้)
              </p>
              <button
                onClick={printLabel}
                disabled={!qr}
                className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
              >
                <Printer size={15} /> พิมพ์ป้าย
              </button>
              <div className="text-[10.5px] text-slate-300 mt-2 break-all">{asset.unique_id || `ASSET #${asset.id}`}</div>
            </div>
          </Card>
        </div>
      </div>

      {editing && (
        <AssetEditor
          mode="edit"
          assetId={asset.id}
          types={types}
          branches={branches}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load() }}
          onDeleted={() => navigate('/manager/assets')}
        />
      )}
    </div>
  )
}
